import { Dtos, SuretyAccountStatus, SuretyBondStatus, SuretyQuoteStatus } from "@inrev/common";
import { useAdminFetchAdminUsers } from "@portal/api/admin-user/admin";
import { useAdminFetchAgencyPreviews } from "@portal/api/agency/admin";
import { useAdminFetchAllEntityTags } from "@portal/api/entity/admin";
import { createContext, ReactNode, useContext, useMemo, useState } from "react";

type PreviewFilterProviderProps = {
	children: ReactNode;
	userType: "agent" | "admin";
};

export enum PreviewFilterTypesEnum {
	STATUS = "status",
	UNDERWRITER = "underwriter",
	AGENCY = "agency",
	TAGS = "tags",
}
export type PreviewFilterType = `${PreviewFilterTypesEnum}`;

export type StatusOptions = SuretyAccountStatus | SuretyQuoteStatus | SuretyBondStatus;

type FilterOptionsToShow = {
	status: StatusOptions[];
	underwriter: Dtos.Admin.AdminUser.Get.Response[];
	agency: Dtos.Admin.Agency.Get.Preview.Response[];
	tags: Dtos.Tag.GetAll.Response;
};

export type ActiveFilters = {
	status?: StatusOptions;
	underwriter?: Dtos.Admin.AdminUser.Get.Response;
	agency?: Dtos.Admin.Agency.Get.Preview.Response;
	tags?: Dtos.Tag.Get.Response[];
};

export type AddToActiveFilterOptions = {
	status?: StatusOptions;
	underwriter?: Dtos.Admin.AdminUser.Get.Response;
	agency?: Dtos.Admin.Agency.Get.Preview.Response;
	tags?: Dtos.Tag.Get.Response;
};

export type SetFiltersDispatchParams = {
	status: StatusOptions[];
	showUnderwriters?: boolean;
	showAgencies?: boolean;
	showTags?: boolean;
};

// Display Config
export type SelectOptions = "single" | "multi";
export const previewFilterDisplayConfig: Record<
	PreviewFilterType,
	{ select: SelectOptions; searchable: boolean }
> = {
	status: {
		select: "single",
		searchable: false,
	},
	underwriter: {
		select: "single",
		searchable: false,
	},
	agency: {
		select: "single",
		searchable: true,
	},
	tags: {
		select: "multi",
		searchable: true,
	},
};

const activeFiltersToColumnFilterMap = (activeFilters: ActiveFilters) => {
	const columnFilters = [];

	// Statuses
	activeFilters.status && columnFilters.push({ id: "status", value: activeFilters.status });

	// Agencies
	activeFilters.agency && columnFilters.push({ id: "agencyId", value: activeFilters.agency.id });

	// Underwriters
	activeFilters.underwriter &&
		columnFilters.push({ id: "underwriter", value: activeFilters.underwriter.id });

	// Tags
	if (activeFilters.tags && activeFilters.tags.length > 0) {
		columnFilters.push({
			id: "entityTags",
			value: activeFilters.tags.map((tag) => tag.id),
		});
	}

	return columnFilters;
};

// Context
type PreviewFilterContext = {
	activeFilters: ActiveFilters;
	columnFilters: { id: string; value: string | string[] | StatusOptions }[];
	filterOptionsToShow: FilterOptionsToShow;
};

type PreviewFilterDispatchContext = {
	setFilterOptions: ({
		status,
		showUnderwriters,
		showAgencies,
		showTags,
	}: SetFiltersDispatchParams) => void;
	addToActiveFilters: <T extends PreviewFilterType>(
		filter: T,
		value: NonNullable<AddToActiveFilterOptions[T]>,
	) => void;
	removeFromActive: {
		(filter: Exclude<PreviewFilterType, "tags">): void;
		(filter: "tags", value: Dtos.Tag.Get.Response): void;
	};
};

const defaultPreviewFilterContext: PreviewFilterContext = {
	activeFilters: {},
	columnFilters: [],
	filterOptionsToShow: {
		status: [],
		underwriter: [],
		agency: [],
		tags: [],
	},
};

const defaultPreviewFilterDispatchContext: PreviewFilterDispatchContext = {
	setFilterOptions: () => {},
	addToActiveFilters: () => {},
	removeFromActive: () => {},
};

const PreviewFilterContext = createContext<PreviewFilterContext>(defaultPreviewFilterContext);

const PreviewFilterDispatchContext = createContext<PreviewFilterDispatchContext>(
	defaultPreviewFilterDispatchContext,
);

// Main Provider
export const PreviewFilterProvider = ({ children, userType }: PreviewFilterProviderProps) => {
	const [activeFilters, setActiveFilters] = useState<ActiveFilters>({});
	const [filterConfig, setFilterConfig] = useState<SetFiltersDispatchParams>({
		status: [],
		showUnderwriters: false,
		showAgencies: false,
		showTags: false,
	});

	// Underwriters, Agencies, Tags will only be fetched for admin users
	const { adminUsers, adminUsersLoading } = useAdminFetchAdminUsers(userType === "agent");
	const { agencyPreviews, agencyPreviewsLoading } = useAdminFetchAgencyPreviews(
		userType === "agent",
	);
	const { entityTags, entityTagsLoading } = useAdminFetchAllEntityTags(userType === "agent");

	const previewFilterContextValue = useMemo(() => {
		const columnFilters = activeFiltersToColumnFilterMap(activeFilters);

		if (adminUsersLoading || agencyPreviewsLoading || entityTagsLoading) {
			return {
				activeFilters,
				columnFilters,
				filterOptionsToShow: {
					status: [],
					underwriter: [],
					agency: [],
					tags: [],
				},
			};
		}

		const filterOptionsToShow = {
			status: [...filterConfig.status],
			underwriter: filterConfig.showUnderwriters && userType === "admin" ? (adminUsers ?? []) : [],
			agency: filterConfig.showAgencies && userType === "admin" ? (agencyPreviews ?? []) : [],
			tags: filterConfig.showTags && userType === "admin" ? (entityTags ?? []) : [],
		};

		return {
			activeFilters,
			columnFilters,
			filterOptionsToShow,
		};
	}, [
		activeFilters,
		filterConfig,
		userType,
		adminUsers,
		adminUsersLoading,
		agencyPreviews,
		agencyPreviewsLoading,
		entityTags,
		entityTagsLoading,
		activeFiltersToColumnFilterMap,
	]);

	const previewFilterDispatchContextValue = useMemo(
		() => ({
			setFilterOptions: ({
				status,
				showUnderwriters = false,
				showAgencies = false,
				showTags = false,
			}: SetFiltersDispatchParams) => {
				// Store the filter configuration
				setFilterConfig({
					status,
					showUnderwriters,
					showAgencies,
					showTags,
				});

				// Reset active filters
				const newActiveFilters: ActiveFilters =
					userType === "admin"
						? { status: undefined, underwriter: undefined, agency: undefined, tags: [] }
						: { status: undefined };

				setActiveFilters(newActiveFilters);
			},
			addToActiveFilters: <T extends PreviewFilterType>(
				filter: T,
				value: NonNullable<AddToActiveFilterOptions[T]>,
			) => {
				if (filter === "tags") {
					setActiveFilters((prev) => {
						const existingTags = prev.tags || [];
						const updatedTags = [...existingTags, value as Dtos.Tag.Get.Response];
						return { ...prev, tags: updatedTags };
					});
					return;
				}
				setActiveFilters((prev) => ({ ...prev, [filter]: value }));
			},
			removeFromActive: (filter: PreviewFilterType, value?: Dtos.Tag.Get.Response) => {
				// Tag filter with specific value: remove that tag from array
				if (filter === "tags" && value) {
					setActiveFilters((prev) => {
						const existingTags = prev.tags || [];
						const updatedTags = existingTags.filter((tag) => tag.id !== value.id);
						return {
							...prev,
							tags: updatedTags.length > 0 ? updatedTags : [],
						};
					});
					return;
				}

				// Any filter without value: clear the filter value
				setActiveFilters((prev) => ({ ...prev, [filter]: undefined }));
			},
		}),
		[userType],
	);

	return (
		<PreviewFilterContext.Provider value={previewFilterContextValue}>
			<PreviewFilterDispatchContext.Provider value={previewFilterDispatchContextValue}>
				{children}
			</PreviewFilterDispatchContext.Provider>
		</PreviewFilterContext.Provider>
	);
};

/**
 * Hook to access the current preview filter state.
 *
 * @returns An object containing:
 * - `activeFilters`: Currently selected filter values. An object with optional keys (status, underwriter, agency, tag).
 *   For status/underwriter/agency, the value is a single item. For tag, the value is an array of tags.
 * - `filterOptionsToShow`: Available options for each filter type that should be displayed in the UI.
 *
 * @example
 * const { activeFilters, filterOptionsToShow } = usePreviewFilter();
 * // activeFilters might be: { status: "active", tag: [...] }
 * // filterOptionsToShow contains all available options for filtering
 */
export const usePreviewFilter = () => {
	return useContext(PreviewFilterContext);
};

/**
 * Hook to access functions for managing preview filters.
 *
 * @returns An object containing:
 * - `setFilters`: Initialize which filter types should be available and their options
 * - `addToActiveFilters`: Add a specific filter value to the active filters (type-safe based on filter type)
 * - `removeFromActive`: Remove a filter or clear all filters
 *
 * @example
 * const { setFilters, addToActiveFilters, removeFromActive } = usePreviewFilterDispatch();
 *
 * // Initialize available filters
 * setFilters({ status: [SuretyAccountStatus.ACTIVE], showTags: true });
 *
 * // Add a specific filter value (type-safe!)
 * addToActiveFilters("status", SuretyAccountStatus.ACTIVE);
 * addToActiveFilters("tag", tag1);
 *
 * // Remove a specific tag from the array
 * removeFromActive("tag", tagToRemove);
 *
 * // Remove entire status filter
 * removeFromActive("status");
 *
 * // Clear all filters
 * removeFromActive();
 */
export const usePreviewFilterDispatch = () => {
	return useContext(PreviewFilterDispatchContext);
};
